"use client";

import {
  useEffect,
  useMemo,
  memo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { format } from "date-fns";
import { getDateFnsLocale } from "@/lib/utils/date-fns-locale";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { BookingDatePicker } from "@/components/product-detail/BookingDatePicker";
import { ResponsiveDatePicker } from "@/components/ui/responsive-date-picker";
import { cn } from "@/lib/utils";
import type {
  ProductDetail,
  ProductRentalBlock,
  BranchOperationBlock,
} from "@/types";
import { calculateRentalDays } from "@/lib/utils/pricing";
import type { SupportedCurrency } from "@/lib/utils/currency";
import type { CheckoutData } from "@/lib/utils/checkout";
import { AddToCartConfirmation } from "@/components/cart/AddToCartConfirmation";
import CartItemsList from "@/components/product-detail/CartItemsList";
import BookingOptionsSection from "@/components/product-detail/BookingOptionsSection";
import DesktopBookingActions from "@/components/product-detail/DesktopBookingActions";
import MobileBookingDrawer from "@/components/product-detail/MobileBookingDrawer";
import BranchSelector from "@/components/product-detail/BranchSelector";
import TimeSelect from "@/components/product-detail/TimeSelect";
import { useWishlist } from "@/hooks/useWishlist";
import { useBookingDateBlocking } from "@/hooks/useBookingDateBlocking";
import { getBlockedTimeslotOrders } from "@/lib/utils/rental-blocks";
import {
  getTimeslotAvailability,
  getSubProductAvailability,
} from "@/app/actions/availability";
import { hasLongTimeOptionLabel } from "@/lib/utils/timeslot-matrix";
import type {
  MergedSubProduct,
  MergedTimeOption,
} from "@/lib/utils/timeslot-matrix";
import {
  getDailyV2BaseRate,
  type MergedDailySubProduct,
} from "@/lib/utils/daily-matrix";
import V2ListBox, { type V2Row } from "@/components/product-detail/V2ListBox";
import { useBookingCart } from "@/hooks/useBookingCart";
import OneWayReturnNotice from "@/components/product-detail/OneWayReturnNotice";
import { getOneWayReturnOption } from "@/lib/mobility/one-way-return";
import { readReturnAtPickup } from "@/lib/mobility/return-at-pickup";
import { isBookingContextReady } from "@/lib/mobility/booking-context";
import type { CartItem } from "@/types/booking";
import {
  MapPinIcon,
  CalendarBlankIcon,
  CaretRightIcon,
  CaretDownIcon,
  CheckIcon,
} from "@phosphor-icons/react";
export type { CartItem } from "@/types/booking";

/** 타임슬롯이 이 개수를 넘으면 리스트에 고정 높이 + 내부 스크롤을 적용한다. */
const TIMESLOT_SCROLL_THRESHOLD = 5;
// v2 긴 라벨 칩 풀폭 스택: 옵션이 이 개수 이상이면 내부 스크롤 (07-17,
// 목업 docs/preview-migration-customer.html A안 — admin timeslot-matrix-preview 미러)
const CHIP_STACK_SCROLL_MIN = 7;

interface BookingCardProps {
  product: ProductDetail;
  locale?: string;
  timeOptions: string[];
  pricingMode: "daily" | "timeslot";
  dailyRate: number;
  timeSlots: Array<{
    id: string;
    durationHours?: number;
    price: number;
    label?: string;
    /** product_pricing.display_order — matched against blocked timeslot_orders. */
    displayOrder: number;
  }>;
  /**
   * Timeslot v2 매트릭스 (Phase B-3) — 언어 병합 완료본(ProductDetailClient).
   * 존재하면 timeslot 모드가 [이용 시간 칩 × 세부 상품 리스트] 선택으로 렌더
   * (v1 timeSlots는 빈 배열 — v1 렌더 경로 불변).
   */
  timeslotMatrix?: {
    timeOptions: MergedTimeOption[];
    subProducts: MergedSubProduct[];
  } | null;
  /**
   * 일일대여 v2 세부 상품 (08-17) — 언어 병합 완료본(ProductDetailClient).
   * 존재하면 daily 모드가 [대여 기간] 아래 [상품 선택 리스트]를 렌더한다.
   * 세부 상품마다 독립적인 기간 티어를 갖는다(결정 2).
   */
  dailyMatrix?: {
    subProducts: MergedDailySubProduct[];
  } | null;
  /**
   * @deprecated Use variant="desktop" instead. Will be removed in future version.
   */
  showButton?: boolean;
  variant?: "desktop" | "mobile";
  cartItems: CartItem[];
  setCartItems: React.Dispatch<React.SetStateAction<CartItem[]>>;
  productBlocks: ProductRentalBlock[];
  branchBlocks: BranchOperationBlock[];
  onAuthRequired?: () => void;
  isAuthenticated?: boolean;
  onChatClick?: () => void;
  isChatLoading?: boolean;
  onCheckout?: (checkoutData: CheckoutData) => void;
  initialPickupDate?: Date;
  initialReturnDate?: Date;
  /** `?branch=` 딥링크가 지정한 픽업 지점 id (설계 §3-4) */
  initialPickupBranch?: string;
  /** 모바일 하단 바 위에 쌓는 원형 버튼 묶음(지도·공유·맨 위로) */
  mobileFloatingActions?: React.ReactNode;
  /** 상세 페이지 공용 찜 상태(이미지 위 하트와 동기화) */
  isWishlisted?: boolean;
  onWishlistChange?: (isWishlisted: boolean) => void;
}

function BookingCard({
  product,
  locale = "ko",
  timeOptions,
  pricingMode,
  dailyRate,
  timeSlots,
  timeslotMatrix,
  dailyMatrix,
  showButton = true,
  variant = "desktop",
  cartItems,
  setCartItems,
  productBlocks,
  branchBlocks,
  onAuthRequired,
  isAuthenticated = false,
  onChatClick,
  isChatLoading = false,
  onCheckout,
  initialPickupDate,
  initialReturnDate,
  initialPickupBranch,
  mobileFloatingActions,
  isWishlisted,
  onWishlistChange,
}: BookingCardProps) {
  const t = useTranslations("product.booking");
  const tMethods = useTranslations("product.usageMethods");
  // 08-20: 리스트 행 가격이 사라져 이 컴포넌트는 금액을 직접 포맷하지 않는다.
  // 통화 포맷은 금액을 실제로 보여주는 하위 컴포넌트(CartItemsList·요약/CTA)가
  // `sourceCurrency`를 받아 각자 수행한다.
  const sourceCurrency: SupportedCurrency = product.currency || "USD";
  // 편도 반납 요금 옵션(파트너 opt-in, 09-22) — 있으면 반납 기본값 = 수령 지점 + 안내.
  const oneWayReturn = useMemo(() => getOneWayReturnOption(product), [product]);
  // 수령한 곳에서 반납(specifications.returnAtPickup, 10-01) — 반납 지점 선택 없이
  // 반납 = 수령 지점으로 고정. 플래그 없는 상품은 기존 동작 그대로.
  const returnAtPickup = readReturnAtPickup(product.specifications);
  const tReturnAtPickup = useTranslations("product.returnAtPickup");
  // 하루당 옵션(09-30)의 현재 선택 — 옵션 상자가 선택을 계속 보여주는 데 쓴다.
  const perDaySelections = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of cartItems) {
      if (item.type === "option" && item.perDay && item.optionId) {
        map[item.optionId] = String(item.optionValue ?? "");
      }
    }
    return map;
  }, [cartItems]);

  // ── rental이 cart에 새로 담기면(모든 필수 선택 완료) 다음 행동을 유도하는 곳으로
  //    부드럽게 스크롤한다(테두리 하이라이트 없음). useBookingCart가 폼 리셋 시점에
  //    onRentalAdded를 호출 → effect 아닌 이벤트라 정확. 모바일 drawer에서는
  //    scrollIntoView가 drawer의 overflow-y-auto 컨테이너를 스크롤한다.
  //    - 옵션이 있으면: 옵션 섹션(필수 옵션 누락 방지)
  //    - 옵션이 없으면: 선택 내역(담겼다는 피드백 + 예약 버튼 노출)
  const optionsRef = useRef<HTMLDivElement>(null);
  const cartRef = useRef<HTMLDivElement>(null);
  const hasOptions = (product.options?.length ?? 0) > 0;

  // 타임슬롯 내부스크롤 안내(하단 페이드 + ↓): 슬롯이 많아 스크롤될 때만,
  // 그리고 바닥에 도달하기 전까지만 표시.
  const slotScrollRef = useRef<HTMLDivElement>(null);
  const [slotAtBottom, setSlotAtBottom] = useState(false);
  const isSlotScrollable = timeSlots.length > TIMESLOT_SCROLL_THRESHOLD;
  const handleSlotScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    setSlotAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 4);
  };

  // v2 긴 라벨 칩 스택 내부 스크롤 안내 — v1 슬롯 리스트 페이드 관례 미러 (07-17)
  const [chipAtBottom, setChipAtBottom] = useState(false);
  const handleChipScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    setChipAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 4);
  };

  const handleRentalAdded = () => {
    // 신규 완성 시 편집 모드 해제 → 다음 완성에서 자동 접힘이 동작
    setEditingContext(false);
    // 자동 스크롤은 모바일 drawer에서만. 데스크톱은 sticky 사이드바라
    // scrollIntoView가 window(페이지)를 위로 점프시키므로 하지 않는다.
    if (variant !== "mobile") return;
    requestAnimationFrame(() => {
      const target = hasOptions ? optionsRef.current : cartRef.current;
      target?.scrollIntoView({
        behavior: "smooth",
        block: hasOptions ? "center" : "end",
      });
    });
  };

  const dateFnsLocale = useMemo(() => getDateFnsLocale(locale), [locale]);

  // ── Hooks ──
  const wishlist = useWishlist({
    productId: product.id,
    isAuthenticated,
    onAuthRequired,
    value: isWishlisted,
    onChange: onWishlistChange,
  });

  const blocking = useBookingDateBlocking({
    product,
    productBlocks,
    branchBlocks,
    selectedPickupBranch: "", // will be overridden after cart hook
    selectedReturnBranch: "",
    pickupDate: undefined,
    returnDate: undefined,
    pickupTime: "",
    timeOptions,
  });

  // ── Timeslot v2 (Phase B-3) ──
  const isTimeslotV2 =
    pricingMode === "timeslot" &&
    !!timeslotMatrix &&
    timeslotMatrix.timeOptions.length > 0 &&
    timeslotMatrix.subProducts.length > 0;
  // v2 세부 상품 잔여 재고 상태 — cart 훅보다 먼저 선언해 map을 훅에 전달한다.
  // (표시용 soldOut/배지는 아래 key-gated memo 사용. 훅에 넘기는 map은 날짜 전환
  //  네트워크 창 동안 직전 응답일 수 있으나 maxQuantity 참고값이라 무해 — 서버가 최종 가드)
  const [subAvailability, setSubAvailability] = useState<{
    key: string;
    map: Map<number, number>;
  } | null>(null);

  // ── Daily v2 (08-17) ──
  // 세부 상품이 하나라도 판매 가능한 티어(1일 기준가 > 0)를 가져야 리스트를 노출한다.
  // `daily_version === 2`인데 이 조건이 깨지면 = 0원 판매 위험 → 리스트 미노출 +
  // 예약 불가(fail-closed). 좌표 없이는 담기 조건이 성립하지 않으므로 결제 경로에
  // 오르지 않는다(useBookingCart daily 분기).
  const dailySellableSubs = useMemo(
    () =>
      (dailyMatrix?.subProducts ?? []).filter(
        (sp) => getDailyV2BaseRate(sp.tiers) > 0,
      ),
    [dailyMatrix],
  );
  const isDailyV2 = pricingMode === "daily" && dailySellableSubs.length > 0;
  /** daily_version=2인데 팔 수 있는 세부 상품이 하나도 없음 → 예약 불가 안내 */
  const dailyV2Unavailable =
    pricingMode === "daily" &&
    product.daily_version === 2 &&
    dailySellableSubs.length === 0;

  const cart = useBookingCart({
    product,
    pricingMode,
    timeSlots,
    timeslotMatrix,
    // daily_version=2면 **판매 가능한 세부 상품이 0개여도** 훅에 넘긴다 —
    // prop 존재 자체가 "이 상품은 세부 상품 좌표 없이는 담기지 않는다"는 신호다.
    // null로 넘기면 v1 daily 경로로 떨어져 빈 pricing으로 0원 라인이 담긴다.
    dailyMatrix:
      pricingMode === "daily" && product.daily_version === 2
        ? { subProducts: dailySellableSubs }
        : null,
    subRemaining: subAvailability?.map,
    cartItems,
    setCartItems,
    locale,
    dateFnsLocale,
    sourceCurrency,
    isAuthenticated,
    onAuthRequired,
    onCheckout,
    variant,
    hasPickupOptions: blocking.hasPickupOptions,
    isDelivery: blocking.isDelivery,
    isShipping: blocking.isShipping,
    productBlocks,
    branchBlocks,
    initialPickupDate,
    initialReturnDate,
    initialPickupBranch,
    returnDefaultsToPickup: oneWayReturn != null,
    returnAtPickup,
    onRentalAdded: handleRentalAdded,
  });

  // Re-call blocking with actual state from cart (React re-render ensures sync)
  const {
    getDisabledDate,
    availablePickupTimeOptions,
    availableReturnTimeOptions,
    hasPickupOptions,
    hasReturnOptions,
    dateSelectionDisabled: _dateSelectionDisabled,
    isDelivery,
    isShipping,
    hasDeliveryMethod,
    hasShippingMethod,
    hasPickupMethod,
  } = useBookingDateBlocking({
    product,
    productBlocks,
    branchBlocks,
    selectedPickupBranch: cart.selectedPickupBranch,
    selectedReturnBranch: cart.selectedReturnBranch,
    pickupDate: cart.pickupDate,
    returnDate: cart.returnDate,
    pickupTime: cart.pickupTime,
    timeOptions,
  });

  const dateSelectionDisabled = hasPickupOptions && !cart.selectedPickupBranch;
  const isTimeSlotDisabled =
    (hasPickupOptions && !cart.selectedPickupBranch) || !cart.pickupDate;
  const disabledDate = getDisabledDate();

  // Per-slot operation restrictions: which timeslot display_orders are blocked on
  // the chosen pickup date (+ branch). Date stays selectable; only these slots
  // are disabled. Empty until a date is picked.
  const blockedSlotOrders = useMemo(() => {
    if (pricingMode !== "timeslot" || !cart.pickupDate)
      return new Set<number>();
    // "delivery"/"shipping" 유사 지점 id 를 그대로 넘긴다 — 택배 제한
    // (method_type 이 있는 행) 은 그 방법을 고른 고객에게만 적용되고,
    // 실제 지점 uuid 와는 겹치지 않으므로 지점-스코프 행은 예전처럼 안 걸린다.
    return getBlockedTimeslotOrders(
      cart.pickupDate,
      product.id,
      cart.selectedPickupBranch || "",
      productBlocks,
    );
  }, [
    pricingMode,
    cart.pickupDate,
    cart.selectedPickupBranch,
    product.id,
    productBlocks,
  ]);

  // ── 슬롯 잔여 재고(정원) — 날짜 선택 시 실시간 조회 (30분 상품 캐시와 분리).
  // display_order → remaining. 조회 결과에 없는 슬롯 = 정원 미설정(무제한).
  // setState는 비동기 응답 콜백에서만 호출하고, 날짜/상품이 바뀐 낡은 응답은
  // key 불일치로 무시한다 (set-state-in-effect 금지 패턴 준수).
  const [slotAvailability, setSlotAvailability] = useState<{
    key: string;
    map: Map<number, number>;
  } | null>(null);
  const hasCapacityLimits = (product.timeslot_capacities?.length ?? 0) > 0;
  const pickupDateISO = cart.pickupDate
    ? `${cart.pickupDate.getFullYear()}-${String(cart.pickupDate.getMonth() + 1).padStart(2, "0")}-${String(cart.pickupDate.getDate()).padStart(2, "0")}`
    : null;
  const availabilityKey =
    pricingMode === "timeslot" && pickupDateISO && hasCapacityLimits
      ? `${product.id}:${pickupDateISO}`
      : null;
  useEffect(() => {
    // 정원이 설정된 슬롯이 하나도 없으면 조회 자체를 생략
    if (!availabilityKey || !pickupDateISO) return;
    let cancelled = false;
    getTimeslotAvailability(product.id, pickupDateISO).then((rows) => {
      if (cancelled) return;
      setSlotAvailability({
        key: availabilityKey,
        map: new Map(rows.map((row) => [row.display_order, row.remaining])),
      });
    });
    return () => {
      cancelled = true;
    };
  }, [availabilityKey, pickupDateISO, product.id]);
  const slotRemaining = useMemo(
    () =>
      slotAvailability && slotAvailability.key === availabilityKey
        ? slotAvailability.map
        : new Map<number, number>(),
    [slotAvailability, availabilityKey],
  );

  // 매진 슬롯 (잔여 0). 수동 마감(blockedSlotOrders)과 동일하게 선택 불가 처리.
  const soldOutSlotOrders = useMemo(() => {
    const set = new Set<number>();
    slotRemaining.forEach((remaining, order) => {
      if (remaining <= 0) set.add(order);
    });
    return set;
  }, [slotRemaining]);

  // ── Timeslot v2 세부 상품 잔여 재고 — v1 조회 패턴 미러 (유한 재고 상품 있을 때만,
  //    비동기 콜백 setState + key 불일치 낡은 응답 무시) ──
  const hasFiniteSubStock = (timeslotMatrix?.subProducts ?? []).some(
    (sp) => sp.totalStock != null,
  );
  const subAvailabilityKey =
    isTimeslotV2 && pickupDateISO && hasFiniteSubStock
      ? `${product.id}:${pickupDateISO}`
      : null;
  useEffect(() => {
    if (!subAvailabilityKey || !pickupDateISO) return;
    let cancelled = false;
    getSubProductAvailability(product.id, pickupDateISO).then((rows) => {
      if (cancelled) return;
      setSubAvailability({
        key: subAvailabilityKey,
        map: new Map(rows.map((row) => [row.sub_display_order, row.remaining])),
      });
    });
    return () => {
      cancelled = true;
    };
  }, [subAvailabilityKey, pickupDateISO, product.id]);
  // remaining 매핑: 0 = 매진, 1~3 = 잔여 배지, 결과에 없는 좌표 = 무제한
  const subRemaining = useMemo(
    () =>
      subAvailability && subAvailability.key === subAvailabilityKey
        ? subAvailability.map
        : new Map<number, number>(),
    [subAvailability, subAvailabilityKey],
  );

  // If the chosen date makes the currently selected slot unavailable, drop it.
  useEffect(() => {
    if (!cart.selectedTimeSlot) return;
    const selectedSlot = timeSlots.find((s) => s.id === cart.selectedTimeSlot);
    if (
      selectedSlot &&
      (blockedSlotOrders.has(selectedSlot.displayOrder) ||
        soldOutSlotOrders.has(selectedSlot.displayOrder))
    ) {
      cart.setSelectedTimeSlot("");
    }
  }, [blockedSlotOrders, soldOutSlotOrders, cart, timeSlots]);

  // cart에 rental이 담겨 있으면 selector 입력 영역을 숨기고, 정보는 상단 요약 박스 +
  // 하단 "선택 내역"으로 분리한다. 단일 건 정책: rental 1건이 담기면 입력 UI를 접는다.
  const hasRentalInCart = cartItems.some((i) => i.type === "rental");

  // 지점/날짜/필요한 시간이 완성되면 입력을 접고 상품 선택은 유지한다.
  // daily와 timeslot 모두 v1/v2에 같은 표시 조건을 사용한다.
  const timeslotTimeRequired =
    pricingMode === "timeslot" &&
    !isDelivery(cart.selectedPickupBranch) &&
    product.is_pickup_time_required !== false;
  const contextReady = isBookingContextReady({
    pricingMode,
    hasPickupOptions,
    hasReturnOptions,
    returnAtPickup,
    pickupBranch: cart.selectedPickupBranch,
    returnBranch: cart.selectedReturnBranch,
    pickupDate: cart.pickupDate,
    returnDate: cart.returnDate,
    pickupTime: cart.pickupTime,
    returnTime: cart.returnTime,
    pickupTimeRequired: pricingMode === "daily"
      ? !isDelivery(cart.selectedPickupBranch) : timeslotTimeRequired,
    returnTimeRequired: !isDelivery(returnAtPickup
      ? cart.selectedPickupBranch : cart.selectedReturnBranch),
  });

  // 타임슬롯 상단 박스 [변경] → 지점/날짜/시간을 값 유지한 채 다시 펼침
  const [editingContext, setEditingContext] = useState(false);

  // 상단 요약: 대여 선택 확정 또는 일정 입력 완성(상품 선택 전).
  const showSummaryBar =
    hasRentalInCart || (contextReady && !editingContext);
  // 일정 입력은 미완성 상태이거나 [변경]으로 편집 중일 때 표시한다.
  const showContextSelectors = !contextReady || editingContext;

  // 지점 id → 표시명 (폼 소스 상단 박스용). delivery/shipping sentinel 처리.
  const branchDisplayName = (branchId: string): string => {
    if (!branchId) return "";
    if (branchId === "delivery") return tMethods("delivery");
    if (branchId === "shipping") return tMethods("shipping");
    return product.branches?.find((b) => b.id === branchId)?.name ?? branchId;
  };

  // Pickup time change handler (clears return time if same-day conflict)
  const handlePickupTimeChange = (value: string) => {
    cart.setPickupTime(value);
    if (
      cart.pickupDate &&
      cart.returnDate &&
      cart.pickupDate.toDateString() === cart.returnDate.toDateString() &&
      cart.returnTime &&
      cart.returnTime <= value
    ) {
      cart.setReturnTime("");
    }
    cart.checkAndAddRentalToCart({ newPickupTime: value });
  };

  const handleReturnTimeChange = (value: string) => {
    cart.setReturnTime(value);
    cart.checkAndAddRentalToCart({ newReturnTime: value });
  };

  // Return time disabled check
  const isReturnTimeDisabled = (time: string) =>
    !!cart.pickupTime &&
    cart.pickupDate?.toDateString() === cart.returnDate?.toDateString() &&
    time <= cart.pickupTime;

  // ── Date trigger button (shared between mobile daily & desktop daily) ──
  const renderDateTriggerButton = (mode: "daily" | "timeslot") => {
    const dateLabel =
      mode === "daily" && cart.pickupDate && cart.returnDate
        ? `${format(cart.pickupDate, "M.d", {
            locale: dateFnsLocale,
          })} ~ ${format(cart.returnDate, "M.d", {
            locale: dateFnsLocale,
          })} · ${t("days", {
            count: calculateRentalDays(cart.pickupDate, cart.returnDate),
          })}`
        : mode === "timeslot" && cart.pickupDate
        ? format(cart.pickupDate, "M.d", { locale: dateFnsLocale })
        : t("dateDrawerTitle");

    return (
      <button
        type="button"
        disabled={dateSelectionDisabled}
        className={cn(
          "flex items-center gap-2 w-full h-9 px-3 rounded-md border border-input bg-transparent text-left text-sm transition-[color,box-shadow] shadow-xs",
          dateSelectionDisabled
            ? "opacity-50 cursor-not-allowed"
            : "cursor-pointer hover:bg-accent hover:text-accent-foreground",
          !cart.pickupDate && !cart.returnDate && "text-muted-foreground",
        )}
        onClick={() => !dateSelectionDisabled && cart.setIsDateDrawerOpen(true)}
      >
        <CalendarBlankIcon className="h-4 w-4 shrink-0" />
        <span className="text-sm">{dateLabel}</span>
      </button>
    );
  };

  // ── Branch Selectors ──
  const renderPickupBranch = () =>
    hasPickupOptions && (
      <div>
        <p className="font-semibold mb-2 text-sm flex items-center gap-2">
          <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full ring-3 bg-white ring-orange-500" />
          {t("pickupBranch")}
        </p>
        <BranchSelector
          value={cart.selectedPickupBranch}
          onValueChange={cart.handlePickupBranchChange}
          placeholder={t("selectPickupLocation")}
          locationType="pickup"
          branches={product.branches || []}
          hasPickupMethod={hasPickupMethod}
          hasShippingMethod={hasShippingMethod}
          hasDeliveryMethod={hasDeliveryMethod}
          partnerCountryCode={product.partner?.country_code}
        />
        {returnAtPickup && renderReturnAtPickupRow()}
      </div>
    );

  /** 수령한 곳에서 반납 상품 — 반납 지점 선택칸 대신 수령 지점을 따라가는 안내 줄. */
  const renderReturnAtPickupRow = () => {
    const pickupName = branchDisplayName(cart.selectedPickupBranch);
    return (
      <div className="mt-2 flex items-start gap-2 rounded-lg bg-gray-50 px-3 py-2.5 text-[13px] text-gray-700">
        <span className="font-semibold text-gray-900 shrink-0">
          {tReturnAtPickup("label")}
        </span>
        {pickupName ? (
          <span className="min-w-0 break-keep">
            {tReturnAtPickup("sameAsPickup")} ·{" "}
            <span className="font-medium text-gray-900">{pickupName}</span>
            <span className="block text-xs text-gray-500 mt-0.5">
              {tReturnAtPickup("dropOffHint")}
            </span>
          </span>
        ) : (
          <span className="min-w-0 break-keep">
            {tReturnAtPickup("beforePickup")}
          </span>
        )}
      </div>
    );
  };

  const renderReturnBranch = () =>
    !returnAtPickup &&
    hasReturnOptions && (
      <div>
        <p className="font-semibold mb-2 text-sm flex items-center gap-2">
          <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full ring-3 bg-white ring-orange-500" />
          {t("returnBranch")}
        </p>
        <BranchSelector
          value={cart.selectedReturnBranch}
          onValueChange={cart.handleReturnBranchChange}
          placeholder={t("selectReturnLocation")}
          locationType="return"
          branches={product.branches || []}
          hasPickupMethod={hasPickupMethod}
          hasShippingMethod={hasShippingMethod}
          hasDeliveryMethod={hasDeliveryMethod}
          partnerCountryCode={product.partner?.country_code}
        />
        {renderOneWayReturnNotice()}
      </div>
    );

  /** 편도 반납 안내(opt-in 상품만) — 문구·가격 포맷은 OneWayReturnNotice 가 맡는다. */
  const renderOneWayReturnNotice = () =>
    oneWayReturn ? (
      <OneWayReturnNotice
        oneWay={oneWayReturn}
        pickupBranchId={cart.selectedPickupBranch}
        returnBranchId={cart.selectedReturnBranch}
        sourceCurrency={sourceCurrency}
      />
    ) : null;

  // ── Daily Time Selects ──
  // 수령/반납 각각 자기 장소 기준으로 노출 (useBookingCart daily 담기 조건과 동일
  // 규칙 — 택배 수령 + 지점 반납에서 반납 시간 칸이 숨겨져 담기지 않던 버그).
  const showPickupTime = !isDelivery(cart.selectedPickupBranch);
  const showReturnTime = !isDelivery(cart.selectedReturnBranch);

  const renderDailyTimeSelects = () =>
    (showPickupTime || showReturnTime) && (
      <div
        className={cn(
          "grid gap-2",
          showPickupTime && showReturnTime ? "grid-cols-2" : "grid-cols-1"
        )}
      >
        {showPickupTime && (
          <TimeSelect
            value={cart.pickupTime}
            onValueChange={handlePickupTimeChange}
            options={availablePickupTimeOptions}
            placeholder={t("pickupTime")}
            disabled={!cart.selectedPickupBranch || !cart.pickupDate}
            emptyMessage={
              !cart.selectedPickupBranch || !cart.pickupDate
                ? t("selectDateFirst")
                : t("noOperatingHours")
            }
          />
        )}
        {showReturnTime && (
          <TimeSelect
            value={cart.returnTime}
            onValueChange={handleReturnTimeChange}
            options={availableReturnTimeOptions}
            placeholder={t("returnTime")}
            disabled={!cart.selectedReturnBranch || !cart.returnDate}
            disabledTime={isReturnTimeDisabled}
            emptyMessage={
              !cart.selectedReturnBranch || !cart.returnDate
                ? t("selectDateFirst")
                : t("noOperatingHours")
            }
          />
        )}
      </div>
    );

  // ── Daily v2 상품 선택 (08-17) — 대여 기간 **아래**, 타임슬롯 v2와 같은 리스트박스
  //    문법. 단일 상품도 항상 1행(이름 비면 상품명 폴백 — 07-17 규칙, 결정 5).
  //    가격은 행에 표시하지 않는다(08-20 확정, preview-bookingcard-price-4modes.html) —
  //    금액은 본문 가격 안내·하단 "선택 내역"에서만. 판매 판정(dailySellableSubs =
  //    1일 기준가 > 0)은 표시와 무관하게 그대로다.
  //    재고 배지·매진은 미노출(결정 8 — daily는 기간 점유라 단일 날짜 재고 모델 부적합).
  const renderDailyV2Selection = () => {
    if (dailyV2Unavailable) {
      return (
        <p className="rounded-md bg-gray-50 px-3 py-2.5 text-sm text-gray-500 break-keep">
          {t("dailyV2Unavailable")}
        </p>
      );
    }
    if (!isDailyV2) return null;

    const rows: V2Row[] = dailySellableSubs.map((sp) => ({
      key: sp.id,
      // 세부 상품명이 비면 상품명 폴백 (전환 직후 단일 상품 케이스)
      main: sp.name || product.name,
      sub: sp.description || undefined,
      selected: cart.selectedSubOrder === sp.displayOrder,
      soldOut: false,
      onSelect: () => {
        cart.setSelectedSubOrder(sp.displayOrder);
        cart.checkAndAddRentalToCart({ newSubOrder: sp.displayOrder });
      },
    }));

    return (
      <div>
        <p className="font-semibold mb-2 text-sm flex items-center gap-2">
          <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full ring-3 bg-white ring-orange-500" />
          {t("selectProduct")}
        </p>
        <V2ListBox
          rows={rows}
          // 날짜보다 먼저 상품을 고를 수 있다(시안 §3 — 기간 미선택 상태에서도 활성).
          disabled={false}
          variant={variant}
          scrollRef={slotScrollRef}
          onScroll={handleSlotScroll}
          atBottom={slotAtBottom}
          soldOutLabel={t("slotUnavailable")}
          remainingLabel={(count) => t("slotRemaining", { count })}
        />
      </div>
    );
  };

  // ── 상단 요약 박스 (SPAO식): "언제/어디"(지점·날짜·시간)를 회색 한 줄 박스 + [변경 >].
  //    데이터 소스 2가지:
  //      - cart 확정(hasRentalInCart): cart rental → [변경]=editRentalItem (selector 재펼침)
  //      - 타임슬롯 컨텍스트 완성(슬롯 전): 폼 state → [변경]=editingContext (값 유지 펼침)
  //    가격을 정하는 슬롯/기간은 하단 선택내역으로 분리하므로 여기엔 넣지 않는다.
  const renderRentalSummaryBar = () => {
    const fmt = (d?: Date) =>
      d ? format(d, "M.d(EEE)", { locale: dateFnsLocale }) : "";

    // 한 구간: [라벨] 날짜·시간 (한 줄) + 지점 (바로 아래 다음 줄).
    // 날짜/시간은 안 자르고(nowrap), 지점만 truncate.
    const leg = (dateTime: string, label?: string, branch?: string) => (
      <div className="min-w-0">
        <div className="flex items-baseline gap-1.5 min-w-0">
          {label && (
            <span className="text-xs text-gray-500 shrink-0">{label}</span>
          )}
          <span className="text-sm font-medium text-gray-900 whitespace-nowrap">
            {dateTime}
          </span>
        </div>
        {branch && (
          <p className="text-xs text-gray-500 truncate mt-0.5">{branch}</p>
        )}
      </div>
    );

    let onChange: () => void;
    let body: ReactNode;

    if (hasRentalInCart) {
      const rental = cartItems.find((i) => i.type === "rental");
      if (!rental) return null;
      const pickupDT = `${fmt(rental.pickupDate)}${
        rental.pickupTime ? ` ${rental.pickupTime}` : ""
      }`;
      if (pricingMode === "daily") {
        const returnDT = `${fmt(rental.returnDate)}${
          rental.returnTime ? ` ${rental.returnTime}` : ""
        }`;
        // 수령/반납 각 구간: 라벨+날짜시간 줄 + 지점 줄
        body = (
          <div className="min-w-0 space-y-2">
            {leg(pickupDT, t("pickupLabel"), rental.pickupBranchName)}
            {leg(
              returnDT,
              t("returnLabel"),
              returnAtPickup && rental.returnBranchName
                ? tReturnAtPickup("sameSuffix", { name: rental.returnBranchName })
                : rental.returnBranchName,
            )}
          </div>
        );
      } else {
        // 타임슬롯: duration은 하단으로, 지점은 날짜 아래 줄로
        body = leg(pickupDT, undefined, rental.pickupBranchName || undefined);
      }
      onChange = () => {
        // 전체 편집 화면을 펼친다(slot 포함). rental 재추가 시 handleRentalAdded가 리셋.
        setEditingContext(true);
        cart.editRentalItem(rental.id);
      };
    } else {
      // 상품 선택 전에도 완성된 일정 입력값을 요약한다.
      const pickupDT = `${fmt(cart.pickupDate)}${
        cart.pickupTime ? ` ${cart.pickupTime}` : ""
      }`;
      body = pricingMode === "daily" ? (
        <div className="min-w-0 space-y-2">
          {leg(pickupDT, t("pickupLabel"), branchDisplayName(cart.selectedPickupBranch))}
          {leg(`${fmt(cart.returnDate)}${cart.returnTime ? ` ${cart.returnTime}` : ""}`,
            t("returnLabel"), branchDisplayName(returnAtPickup
              ? cart.selectedPickupBranch : cart.selectedReturnBranch))}
        </div>
      ) : leg(pickupDT, undefined, branchDisplayName(cart.selectedPickupBranch) || undefined);
      onChange = () => setEditingContext(true);
    }

    return (
      <div className="flex items-start justify-between gap-3 rounded-md bg-orange-50 px-3 py-3">
        <div className="flex items-start gap-2 min-w-0">
          <MapPinIcon
            weight="fill"
            className="h-4 w-4 text-orange-500 shrink-0 mt-0.5"
          />
          {body}
        </div>
        <button
          type="button"
          onClick={onChange}
          className="flex items-center gap-0.5 text-xs text-gray-500 hover:text-gray-700 shrink-0"
        >
          {t("change")}
          <CaretRightIcon className="h-4 w-4" />
        </button>
      </div>
    );
  };

  // ── v2 상품 선택 리스트 박스 — 컴포넌트 추출본(V2ListBox)에 위임 (08-17).
  //    마크업은 07-17 확정 그대로(무회귀). 타임슬롯 v2 · 일일대여 v2 공용.
  const renderV2ListBox = (rows: V2Row[]) => (
    <V2ListBox
      rows={rows}
      disabled={isTimeSlotDisabled}
      variant={variant}
      scrollRef={slotScrollRef}
      onScroll={handleSlotScroll}
      atBottom={slotAtBottom}
      soldOutLabel={t("slotUnavailable")}
      remainingLabel={(count) => t("slotRemaining", { count })}
    />
  );

  // ── Timeslot v2 선택 영역 (07-17 사용자 확정: 단일 상품 특례 제거 — 상품 개수와
  //    무관하게 항상 [이용 시간 칩] + [상품 선택 리스트], 단일 상품도 1행 리스트) ──
  const renderTimeslotV2Selection = () => {
    if (!timeslotMatrix) return null;
    const { timeOptions: v2Options, subProducts } = timeslotMatrix;
    const selOptIdx = Math.max(
      0,
      v2Options.findIndex(
        (opt) => opt.displayOrder === cart.selectedTimeOptionOrder,
      ),
    );
    // 긴 라벨(하나라도 12자 초과) → 칩 풀폭 세로 스택 (07-17 A안, admin 미리보기 미러)
    const longLabelStack = hasLongTimeOptionLabel(
      v2Options.map((o) => o.label),
    );
    // 스택 + 옵션 7개 이상 → 내부 스크롤 (6개 이하 스택·인라인 모드 불변)
    const chipStackScrollable =
      longLabelStack && v2Options.length >= CHIP_STACK_SCROLL_MIN;
    const sectionTitle = (label: string) => (
      <p className="font-semibold mb-4 text-sm flex items-center gap-2">
        <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full ring-3 bg-white ring-orange-500" />
        {label}
      </p>
    );

    return (
      <>
        {/* 이용 시간 칩 — 선택 = 가격 축. 08-20부터 리스트에 가격을 표시하지 않으므로
            칩 전환은 "판매 여부(0원 셀 미노출)"와 담기 단가에만 반영된다.
            긴 라벨(하나라도 12자 초과)이면 풀폭 세로 스택 자동 전환 (07-17 A안) */}
        <div>
          {sectionTitle(t("timeOptionsTitle"))}
          <div className="relative">
          <div
            onScroll={chipStackScrollable ? handleChipScroll : undefined}
            className={cn(
              "gap-2",
              longLabelStack ? "flex flex-col" : "flex flex-wrap",
              // 옵션 7개+ 스택: 내부 스크롤 (v1 슬롯 리스트 관례 — max-h-72)
              chipStackScrollable &&
                "max-h-72 overflow-y-auto overscroll-contain",
            )}
          >
            {v2Options.map((opt, idx) => {
              const chipSelected =
                cart.selectedTimeOptionOrder === opt.displayOrder;
              return (
                <button
                  key={opt.id}
                  type="button"
                  disabled={isTimeSlotDisabled}
                  className={cn(
                    // text-left: 긴 라벨 줄바꿈 시 가운데 정렬로 보이는 것 방지 (07-17)
                    "rounded-[10px] border px-4 py-2 text-left text-sm font-semibold transition-colors",
                    // 스택 모드: 풀폭 + keep-all 줄바꿈(2줄 허용)
                    longLabelStack && "w-full break-keep",
                    chipSelected
                      ? "border-orange-500 bg-orange-50 text-orange-700"
                      : "border-gray-300 bg-white text-gray-700",
                    isTimeSlotDisabled
                      ? "opacity-50 cursor-not-allowed"
                      : !chipSelected && "hover:border-gray-400",
                  )}
                  onClick={() => {
                    cart.setSelectedTimeOptionOrder(opt.displayOrder);
                    // 칩 전환으로 선택 중이던 상품이 0원(미노출)이 되면 선택 해제 (07-17)
                    if (cart.selectedSubOrder != null) {
                      const newIdx = v2Options.findIndex(
                        (o) => o.displayOrder === opt.displayOrder,
                      );
                      const selectedSp = subProducts.find(
                        (sp) => sp.displayOrder === cart.selectedSubOrder,
                      );
                      if (
                        selectedSp &&
                        (selectedSp.prices[newIdx] ?? 0) <= 0
                      ) {
                        cart.setSelectedSubOrder(null);
                      }
                    }
                  }}
                >
                  {opt.label || `#${idx + 1}`}
                </button>
              );
            })}
          </div>
          {/* 내부스크롤 안내: 하단 페이드 + ↓ (v1 슬롯 리스트 관례 미러 — 카드 흰 배경).
              화살표에 원형 회색 배경 — 페이드 위 가시성 강화 (07-17, v1 화살표는 불변) */}
          {chipStackScrollable && !chipAtBottom && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-9 flex items-end justify-center bg-linear-to-t from-white to-transparent pb-0.5">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-500/70 backdrop-blur-sm animate-bounce">
                <CaretDownIcon className="h-4 w-4 text-white" />
              </span>
            </div>
          )}
          </div>
        </div>

        {/* 상품 선택 — 선택 즉시 cart 담김(checkAndAddRentalToCart 재사용).
            0원 상품 미노출 (07-17): 선택 칩 기준 가격 미설정(≤0) = 미판매 —
            PricingInfo(>0만)·재가격 fail-closed와 동일 원칙. 전 상품 0원이면
            리스트 박스 숨김(v1 timeSlots.length>0 && 관례). */}
        <div>
          {sectionTitle(t("selectProduct"))}
          {(() => {
            const sellableSubProducts = subProducts.filter(
              (sp) => (sp.prices[selOptIdx] ?? 0) > 0,
            );
            if (sellableSubProducts.length === 0) return null;
            return renderV2ListBox(
              sellableSubProducts.map((sp) => {
                const remaining = subRemaining.get(sp.displayOrder);
                return {
                  key: sp.id,
                  main: sp.name || "-",
                  // 07-17: 재고 표기(무제한/최대N) 미노출 — 설명은 있을 때만.
                  // 잔여 1~3 배지·매진은 유한 재고 데이터(RPC 결과)가 있을 때만 (기존 로직 그대로).
                  sub: sp.description || undefined,
                  selected: cart.selectedSubOrder === sp.displayOrder,
                  soldOut: remaining !== undefined && remaining <= 0,
                  remaining,
                  onSelect: () => {
                    cart.setSelectedSubOrder(sp.displayOrder);
                    cart.checkAndAddRentalToCart({ newSubOrder: sp.displayOrder });
                  },
                };
              }),
            );
          })()}
        </div>
      </>
    );
  };

  // ── Booking Content ──
  const bookingContent = (
    <div className="space-y-3">
      {/* 상단 요약 박스 ("언제/어디") — cart 확정 또는 타임슬롯 컨텍스트 완성 시 */}
      {showSummaryBar && renderRentalSummaryBar()}
      {/* 입력 영역 — rental이 cart에 담기면(선택 완료) 통째로 숨기고, 정보는
          하단 "선택 내역" 카드 + [변경]으로 내린다. 옵션이 자연히 위로 올라온다. */}
      {!hasRentalInCart &&
        (pricingMode === "daily" ? (
          variant === "mobile" ? (
            /* ── Mobile Daily ── */
            <div className="space-y-4">
              {showContextSelectors && <>
              <div className="space-y-3">
                {renderPickupBranch()}
                {renderReturnBranch()}
              </div>
              <div className="space-y-3">
                <div>
                  <p className="font-semibold mb-2 text-sm flex items-center gap-2">
                    <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full ring-3 bg-white ring-orange-500" />
                    {t("rentalPeriod")}
                  </p>
                  {renderDateTriggerButton("daily")}
                  <BookingDatePicker
                    open={!dateSelectionDisabled && cart.isDateDrawerOpen}
                    onOpenChange={cart.setIsDateDrawerOpen}
                    mode="daily"
                    pickupDate={cart.pickupDate}
                    returnDate={cart.returnDate}
                    onApply={(pickup, ret) => {
                      cart.setPickupDate(pickup);
                      if (ret) cart.setReturnDate(ret);
                      cart.checkAndAddRentalToCart({
                        newPickupDate: pickup,
                        newReturnDate: ret,
                      });
                    }}
                    disabledDate={disabledDate}
                    locale={dateFnsLocale}
                  />
                </div>
                {renderDailyTimeSelects()}
              </div>
              </>}
              {/* 상품 선택은 일정 입력이 접혀도 유지한다 (daily v1/v2). */}
              {renderDailyV2Selection()}
            </div>
          ) : (
            /* ── Desktop Daily ── */
            <div className="space-y-4">
              {showContextSelectors && <>
              <div className="space-y-3">
                {renderPickupBranch()}
                {renderReturnBranch()}
              </div>
              <div className="space-y-3">
                <div>
                  <p className="font-semibold mb-2 text-sm flex items-center gap-2">
                    <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full ring-3 bg-white ring-orange-500" />
                    {t("rentalPeriod")}
                  </p>
                  {renderDateTriggerButton("daily")}
                  <BookingDatePicker
                    variant="dialog"
                    open={!dateSelectionDisabled && cart.isDateDrawerOpen}
                    onOpenChange={cart.setIsDateDrawerOpen}
                    mode="daily"
                    pickupDate={cart.pickupDate}
                    returnDate={cart.returnDate}
                    onApply={(pickup, ret) => {
                      cart.setPickupDate(pickup);
                      if (ret) cart.setReturnDate(ret);
                      cart.checkAndAddRentalToCart({
                        newPickupDate: pickup,
                        newReturnDate: ret,
                      });
                    }}
                    disabledDate={disabledDate}
                    locale={dateFnsLocale}
                  />
                </div>
                {renderDailyTimeSelects()}
              </div>
              </>}
              {/* 상품 선택은 일정 입력이 접혀도 유지한다 (daily v1/v2). */}
              {renderDailyV2Selection()}
            </div>
          )
        ) : (
          /* ── Timeslot Mode ── */
          <div className="space-y-6">
            {/* 지점/날짜/시간 — 컨텍스트 완성되면 상단 요약 박스로 접힘. 슬롯 리스트는 유지 */}
            {showContextSelectors && (
              <div>
                <p className="font-semibold mb-4 text-sm flex items-center gap-2">
                  <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full ring-3 bg-white ring-orange-500" />
                  {t("rental")}
                </p>
                <div className="space-y-2">
                  {hasPickupOptions && (
                    <BranchSelector
                      value={cart.selectedPickupBranch}
                      onValueChange={cart.handlePickupBranchChange}
                      placeholder={t("selectPickupLocation")}
                      locationType="pickup"
                      branches={product.branches || []}
                      hasPickupMethod={hasPickupMethod}
                      hasShippingMethod={hasShippingMethod}
                      hasDeliveryMethod={hasDeliveryMethod}
                      partnerCountryCode={product.partner?.country_code}
                    />
                  )}

                  {variant === "mobile" ? (
                    <div className="space-y-2">
                      {renderDateTriggerButton("timeslot")}
                      <BookingDatePicker
                        open={!dateSelectionDisabled && cart.isDateDrawerOpen}
                        onOpenChange={cart.setIsDateDrawerOpen}
                        mode="timeslot"
                        pickupDate={cart.pickupDate}
                        onApply={(date) => {
                          cart.setPickupDate(date);
                          cart.checkAndAddRentalToCart({ newPickupDate: date });
                        }}
                        disabledDate={disabledDate}
                        locale={dateFnsLocale}
                      />
                      {!isDelivery(cart.selectedPickupBranch) &&
                        product.is_pickup_time_required !== false && (
                          <TimeSelect
                            value={cart.pickupTime}
                            onValueChange={(value) => {
                              cart.setPickupTime(value);
                              cart.checkAndAddRentalToCart({
                                newPickupTime: value,
                              });
                            }}
                            options={availablePickupTimeOptions}
                            placeholder={t("startTime")}
                            disabled={
                              !cart.selectedPickupBranch || !cart.pickupDate
                            }
                            emptyMessage={
                              !cart.selectedPickupBranch || !cart.pickupDate
                                ? t("selectDateFirst")
                                : t("noOperatingHours")
                            }
                          />
                        )}
                    </div>
                  ) : (
                    <>
                      <div
                        className={cn(
                          "gap-2",
                          isDelivery(cart.selectedPickupBranch) ||
                            product.is_pickup_time_required === false
                            ? "grid grid-cols-1"
                            : "grid grid-cols-2",
                        )}
                      >
                        <ResponsiveDatePicker
                          open={!dateSelectionDisabled && cart.isPickupOpen}
                          onOpenChange={
                            dateSelectionDisabled
                              ? undefined
                              : cart.setIsPickupOpen
                          }
                          selected={cart.pickupDate}
                          onSelect={(date) => {
                            cart.setPickupDate(date);
                            cart.checkAndAddRentalToCart({
                              newPickupDate: date,
                            });
                          }}
                          disabled={disabledDate}
                          title={t("selectDate")}
                          locale={dateFnsLocale}
                          trigger={
                            <Button
                              variant="outline"
                              disabled={dateSelectionDisabled}
                              className={cn(
                                "justify-start text-left font-normal px-3 w-full",
                                dateSelectionDisabled &&
                                  "opacity-50 cursor-not-allowed",
                                !cart.pickupDate && "text-muted-foreground",
                              )}
                            >
                              <CalendarBlankIcon className="h-4 w-4 shrink-0" />
                              <span className="text-sm">
                                {cart.pickupDate
                                  ? format(cart.pickupDate, "yyyy. M. d.", {
                                      locale: dateFnsLocale,
                                    })
                                  : t("date")}
                              </span>
                            </Button>
                          }
                        />

                        {!isDelivery(cart.selectedPickupBranch) &&
                          product.is_pickup_time_required !== false && (
                            <TimeSelect
                              value={cart.pickupTime}
                              onValueChange={(value) => {
                                cart.setPickupTime(value);
                                cart.checkAndAddRentalToCart({
                                  newPickupTime: value,
                                });
                              }}
                              options={availablePickupTimeOptions}
                              placeholder={t("time")}
                              disabled={
                                !cart.selectedPickupBranch || !cart.pickupDate
                              }
                              emptyMessage={t("noOperatingHours")}
                            />
                          )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Timeslot v2 (Phase B-3): 이용 시간 칩 + 상품 선택 리스트 — v1 블록은 아래 else 그대로 */}
            {isTimeslotV2 ? (
              renderTimeslotV2Selection()
            ) : (
            <div>
              <p className="font-semibold mb-4 text-sm flex items-center gap-2">
                <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full ring-3 bg-white ring-orange-500" />
                {t("rentalDuration")}
              </p>
              {timeSlots.length > 0 && (
                // 전체를 하나의 박스로. 연한 배경 + 약한 라운드.
                // 날짜 선택(활성)되면 테두리를 진하게 해 입력 가능함을 강조.
                <div
                  className={cn(
                    "relative rounded-md border overflow-hidden bg-gray-50",
                    isTimeSlotDisabled ? "border-gray-200" : "border-gray-300",
                  )}
                >
                  <div
                    ref={slotScrollRef}
                    onScroll={isSlotScrollable ? handleSlotScroll : undefined}
                    className={cn(
                      "divide-y divide-gray-200",
                      // 슬롯이 많으면 고정 높이 내부 스크롤 → 하단 옵션이 밀리지 않게.
                      // 마지막 항목이 반쯤 보이도록 max-h를 잡아 "더 있음" 신호.
                      isSlotScrollable &&
                        "overflow-y-auto overscroll-contain " +
                          (variant === "mobile" ? "max-h-60" : "max-h-72"),
                    )}
                  >
                    {timeSlots.map((slot) => {
                      const selected = cart.selectedTimeSlot === slot.id;
                      // Slot-scoped operation block on the chosen date → this slot
                      // is closed even though the date itself stays selectable.
                      const slotBlocked = blockedSlotOrders.has(
                        slot.displayOrder,
                      );
                      // 정원(재고) 소진 — 수동 마감과 동일하게 "매진" 표기로 통일
                      const slotSoldOut = soldOutSlotOrders.has(
                        slot.displayOrder,
                      );
                      const slotUnavailable = slotBlocked || slotSoldOut;
                      const slotDisabled = isTimeSlotDisabled || slotUnavailable;
                      // 잔여 1~3일 때만 배지 표시 (무제한/여유 슬롯은 미표시)
                      const remaining = slotRemaining.get(slot.displayOrder);
                      const showRemainingBadge =
                        !slotDisabled &&
                        remaining !== undefined &&
                        remaining >= 1 &&
                        remaining <= 3;
                      // 08-20: 슬롯 행에 가격을 표시하지 않는다(확정 시안). 성수기
                      // 재정의(getSeasonalTimeslotPrice — v1은 sub=null, v2는
                      // (sub, option) 행)는 담기/재가격 경로(useBookingCart·
                      // cart.ts·checkout.ts)가 각자 계산하므로 여기서는 표시 전용
                      // 계산이 사라진다.
                      return (
                        <button
                          key={slot.id}
                          type="button"
                          disabled={slotDisabled}
                          className={cn(
                            "flex items-center justify-between w-full py-2.5 px-3 text-left text-sm transition-colors gap-3",
                            slotDisabled
                              ? "opacity-50 cursor-not-allowed"
                              : selected
                              ? "bg-orange-50 cursor-pointer"
                              : "hover:bg-gray-100 cursor-pointer",
                          )}
                          onClick={() => {
                            if (slotUnavailable) return;
                            cart.setSelectedTimeSlot(slot.id);
                            cart.checkAndAddRentalToCart({
                              newTimeSlot: slot.id,
                            });
                          }}
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            {/* 체크박스 (단일 선택이지만 이미지처럼 사각 체크) */}
                            <span
                              className={cn(
                                "flex items-center justify-center w-5 h-5 rounded border shrink-0 transition-colors",
                                selected
                                  ? "bg-orange-500 border-orange-500"
                                  : "bg-white border-gray-300",
                              )}
                            >
                              {selected && (
                                <CheckIcon
                                  className="w-3.5 h-3.5 text-white"
                                  weight="bold"
                                />
                              )}
                            </span>
                            <div className="min-w-0">
                              {slot.label && (
                                <span
                                  className={cn(
                                    "block truncate",
                                    selected
                                      ? "text-orange-700"
                                      : slot.durationHours
                                      ? "text-gray-700"
                                      : "text-gray-900",
                                    !slot.durationHours && "font-medium",
                                  )}
                                >
                                  {slot.label}
                                </span>
                              )}
                              {slot.durationHours && (
                                <span
                                  className={cn(
                                    "block font-medium",
                                    selected
                                      ? "text-orange-700"
                                      : "text-gray-900",
                                  )}
                                >
                                  {t("hours", { count: slot.durationHours })}
                                </span>
                              )}
                            </div>
                          </div>
                          {slotUnavailable ? (
                            <span className="whitespace-nowrap shrink-0 text-xs font-medium text-red-500">
                              {t("slotUnavailable")}
                            </span>
                          ) : (
                            /* 가격 미표시(08-20) — 우측 슬롯에는 잔여 배지만 남는다. */
                            <span className="flex items-center gap-2 shrink-0">
                              {showRemainingBadge && (
                                <span className="whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700">
                                  {t("slotRemaining", { count: remaining })}
                                </span>
                              )}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {/* 내부스크롤 안내: 하단 페이드 + ↓ (바닥 도달 전까지만) */}
                  {isSlotScrollable && !slotAtBottom && (
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-9 flex items-end justify-center bg-linear-to-t from-gray-100 to-transparent pb-0.5">
                      <CaretDownIcon className="h-4 w-4 text-gray-500 animate-bounce" />
                    </div>
                  )}
                </div>
              )}
            </div>
            )}
          </div>
        ))}

      {/* Options */}
      <div ref={optionsRef} className="scroll-mt-4">
        <BookingOptionsSection
          options={product.options || []}
          sourceCurrency={sourceCurrency}
          selectedOptions={cart.selectedOptions}
          missingRequiredOptionIds={cart.missingRequiredOptionIds}
          onOptionSelect={(optionId, value) => {
            cart.setSelectedOptions((prev) => ({
              ...prev,
              [optionId]: { enabled: true, value },
            }));
            cart.addOptionToCart(optionId, value);
          }}
          perDayOrders={cart.perDayOrders}
          perDayDays={cart.perDayDays}
          perDaySelections={perDaySelections}
        />
      </div>

      {/* Cart Items */}
      <div ref={cartRef} className="scroll-mt-4">
        <CartItemsList
          cartItems={cartItems}
          pricingMode={pricingMode}
          productName={product.name}
          pricing={product.pricing || []}
          seasons={product.pricing_seasons}
          dayPrices={product.day_prices}
          policyTable={product.policyTable}
          sourceCurrency={sourceCurrency}
          totalPrice={cart.totalPrice}
          perDayDays={cart.perDayDays}
          onQuantityChange={cart.updateCartItemQuantity}
          onRemove={cart.removeCartItem}
        />
      </div>

      {/* Total Price & Reserve Button - Desktop only */}
      {variant === "desktop" && showButton && (
        <DesktopBookingActions
          cartItemCount={cartItems.length}
          isAddingToCart={cart.isAddingToCart}
          hasMissingRequiredOptions={cart.hasMissingRequiredOptions}
          isChatLoading={isChatLoading}
          isWishlisted={wishlist.isWishlisted}
          isWishlistLoading={wishlist.isWishlistLoading}
          onCheckout={cart.handleCheckout}
          onAddToCart={cart.handleAddToCart}
          onChatClick={onChatClick}
          onWishlistClick={wishlist.handleWishlistClick}
          depositMethods={product.deposit_methods}
          depositAmount={product.deposit_amount}
          sourceCurrency={sourceCurrency}
        />
      )}
    </div>
  );

  // Mobile variant: wrap in Drawer
  if (variant === "mobile") {
    return (
      <>
        <MobileBookingDrawer
          open={cart.isDrawerOpen}
          onOpenChange={cart.setIsDrawerOpen}
          cartItemCount={cartItems.length}
          isAddingToCart={cart.isAddingToCart}
          hasMissingRequiredOptions={cart.hasMissingRequiredOptions}
          isChatLoading={isChatLoading}
          onCheckout={cart.handleCheckout}
          onAddToCart={cart.handleAddToCart}
          onChatClick={onChatClick}
          depositMethods={product.deposit_methods}
          depositAmount={product.deposit_amount}
          sourceCurrency={sourceCurrency}
          isWishlisted={wishlist.isWishlisted}
          isWishlistLoading={wishlist.isWishlistLoading}
          onWishlistClick={wishlist.handleWishlistClick}
          floatingActions={mobileFloatingActions}
        >
          {bookingContent}
        </MobileBookingDrawer>
        <AddToCartConfirmation
          open={cart.showCartConfirmation}
          onOpenChange={cart.setShowCartConfirmation}
        />
        {cart.addToCartGuardDialog}
      </>
    );
  }

  // Desktop variant
  return (
    <>
      {bookingContent}
      <AddToCartConfirmation
        open={cart.showCartConfirmation}
        onOpenChange={cart.setShowCartConfirmation}
      />
    </>
  );
}

export default memo(BookingCard);
